import React, { useState } from 'react';
import { ChevronDownIcon } from 'lucide-react';
import type { PolicySection } from '../../types/policy';
interface PolicyContentsProps {
  sections: PolicySection[];
  activeId: string;
  /** Accessible name for the section index, e.g. "Privacy Policy sections". */
  label?: string;
}
const focusRing = 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-600 focus-visible:ring-offset-2 focus-visible:ring-offset-ivory';
export function PolicyContents({
  sections,
  activeId,
  label = 'Privacy Policy sections'
}: PolicyContentsProps) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const activeSection = sections.find(section => section.id === activeId);
  return <>
      {/* Mobile: collapsible contents */}
      <div className="lg:hidden sticky top-[77px] md:top-[89px] z-30 -mx-6 px-6 py-3 bg-ivory/95 backdrop-blur-sm border-b border-charcoal/10" data-id="element-271" data-mp="3o9jjuc5nzwl1">
        <button type="button" onClick={() => setMobileOpen(open => !open)} aria-expanded={mobileOpen} aria-controls="policy-contents-mobile" className={`w-full flex items-center justify-between gap-3 rounded-xl border border-purple-200 bg-white px-4 py-3 text-left ${focusRing}`} data-id="element-272" data-mp="3j9dpupbx9xzs">
          <span className="min-w-0" data-id="element-273" data-mp="22tm3bgtuyv01">
            <span className="block text-[11px] font-semibold uppercase tracking-[0.14em] text-purple-700" data-id="element-274" data-mp="27utb3fw03u6p">
              Contents
            </span>
            <span className="block truncate text-sm text-charcoal/80" data-id="element-275" data-mp="1a4oknclqiw6r">
              {activeSection ? `${activeSection.number}. ${activeSection.title}` : 'Jump to a section'}
            </span>
          </span>
          <ChevronDownIcon aria-hidden="true" className={`w-5 h-5 shrink-0 text-purple-700 transition-transform duration-200 ease-out ${mobileOpen ? 'rotate-180' : ''}`} data-id="element-276" data-mp="1ow7ae24ec53b" />
        </button>

        {mobileOpen && <nav id="policy-contents-mobile" aria-label={label} className="mt-2 max-h-[60vh] overflow-y-auto rounded-xl border border-purple-200 bg-white py-2" data-id="element-277" data-mp="oeowoaauyoje">
            <ol className="text-sm" data-id="element-278" data-mp="y48gbi6eamfb">
              {sections.map(section => {
            const isActive = section.id === activeId;
            return <li key={section.id} data-id="element-279" data-mp="t66u4odoa6zm">
                    <a href={`#${section.id}`} onClick={() => setMobileOpen(false)} aria-current={isActive ? 'true' : undefined} className={`flex gap-2 px-4 py-2.5 ${focusRing} ${isActive ? 'text-purple-800 font-semibold bg-purple-50' : 'text-charcoal/75 hover:text-purple-800 hover:bg-purple-50'}`} data-id="element-280" data-mp="5fax9cud0lli">
                      <span className="tabular-nums text-charcoal/40" data-id="element-281" data-mp="1luvq68zvfm4i">
                        {section.number}.
                      </span>
                      <span data-id="element-282" data-mp="1tcadulpa7oof">{section.title}</span>
                    </a>
                  </li>;
          })}
            </ol>
          </nav>}
      </div>

      {/* Desktop: sticky index */}
      <nav aria-label={label} className="hidden lg:block sticky top-32 max-h-[calc(100vh-11rem)] overflow-y-auto pr-4" data-id="element-283" data-mp="1kf9sknxie6p7">
        <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-purple-700 mb-4" data-id="element-284" data-mp="2c3tfao3nquoc">
          On this page
        </p>
        <ol className="space-y-1 border-l border-charcoal/10" data-id="element-285" data-mp="2h2efiniwtecs">
          {sections.map(section => {
          const isActive = section.id === activeId;
          return <li key={section.id} data-id="element-286" data-mp="27c1wx0unnv9a">
                <a href={`#${section.id}`} aria-current={isActive ? 'true' : undefined} className={`block -ml-px border-l-2 pl-4 py-1.5 text-sm leading-snug transition-colors duration-200 ease-out ${focusRing} ${isActive ? 'border-purple-600 text-purple-800 font-semibold' : 'border-transparent text-charcoal/65 hover:text-purple-800 hover:border-purple-300'}`} data-id="element-287" data-mp="27b09dwwgdgao">
                  <span className="tabular-nums text-charcoal/40 mr-1.5" data-id="element-288" data-mp="y5ul63myo9ab">
                    {section.number}.
                  </span>
                  {section.title}
                </a>
              </li>;
        })}
        </ol>
      </nav>
    </>;
}